import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';

import { TextButton, SecondarySolidButton, SecondaryTextButton } from '@bibliocommons/base-buttons';
import { ArtificialIntelligence, Feedback } from '@bibliocommons/base-icons';

import ExpandableText from 'app/components/shared/Expandable/ExpandableText';
import { ModalHeader, ModalContent, ModalFooter } from 'app/components/shared/Modal';
import ModalContainer from 'app/components/shared/Modal/ModalContainer';

import ShareFeedbackModalContainer from './ShareFeedbackModalContainer';
import { FEEDBACK_MODAL_KEY } from './ShareFeedbackModal';

import './CommentSummary.scss';

const ABOUT_MODAL_KEY = 'comment-summary-about-modal';
// Product decision 2026-08-11; expected to be tuned during preview experimentation
const SUMMARY_TRUNCATE_COUNT = 325;

export default function CommentSummary({ commentSummary, modalActions }) {
  const openAboutModal = () => modalActions.openModal(ABOUT_MODAL_KEY, { authRequired: false });

  const closeAboutModal = () => modalActions.closeModal(ABOUT_MODAL_KEY);

  // Default authRequired: a logged-out click opens the login modal first, and the
  // openModal action replays after login (authMiddleware's preLoginAction).
  const handleShareFeedback = () => modalActions.openModal(FEEDBACK_MODAL_KEY);

  function renderAboutModal() {
    return (
      <ModalContainer modalKey={ABOUT_MODAL_KEY} handleClose={closeAboutModal}>
        <ModalHeader title={<FormattedMessage id="about_this_content" />} />
        <ModalContent dataKey="comment-summary-about-body">
          <p>
            <FormattedMessage id="comment_summary_about_p1" />
          </p>
          <p>
            <FormattedMessage id="comment_summary_about_p2" />
          </p>
          <p>
            <FormattedMessage id="comment_summary_about_p3" />
          </p>
        </ModalContent>
        <ModalFooter
          renderPrimaryAction={() => null}
          renderSecondaryAction={() => (
            <SecondarySolidButton
              className="modal-footer__secondary-button"
              dataKey="comment-summary-about-close"
              handleClick={closeAboutModal}
            >
              <FormattedMessage id="button_close" />
            </SecondarySolidButton>
          )}
        />
      </ModalContainer>
    );
  }

  const summaryText = commentSummary.get('summary');

  return (
    <div className="cp-comment-summary" data-key="comment-summary-panel">
      <p className="comment-summary-heading" data-key="comment-summary-heading">
        <FormattedMessage id="comment_summary_heading" />
      </p>
      <p className="comment-summary-disclaimer" data-key="comment-summary-disclaimer">
        <ArtificialIntelligence />
        <span className="disclaimer-text">
          <FormattedMessage id="comment_summary_disclaimer" />
        </span>
      </p>
      <ExpandableText
        className="comment-summary-text"
        dataKey="comment-summary-text"
        text={summaryText}
        textKey="comment_summary"
        truncateCount={SUMMARY_TRUNCATE_COUNT}
      />
      <div className="comment-summary-actions">
        <TextButton
          className="share-feedback-button"
          dataKey="comment-summary-share-feedback"
          handleClick={handleShareFeedback}
          renderLeadingIcon={() => <Feedback height={16} width={16} />}
          size="small"
        >
          <FormattedMessage id="share_feedback" />
        </TextButton>
        <SecondaryTextButton
          className="why-seeing-this-button"
          dataKey="comment-summary-about-button"
          handleClick={openAboutModal}
          size="small"
        >
          <FormattedMessage id="why_seeing_this" />
        </SecondaryTextButton>
      </div>
      {renderAboutModal()}
      <ShareFeedbackModalContainer />
    </div>
  );
}

CommentSummary.propTypes = {
  commentSummary: ImmutablePropTypes.map.isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired
};
